import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, Link, Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useUser } from '../features/auth/useUser';
import { useLogout } from '../hooks/useLogout';
import { useImpersonation } from '../features/impersonation/useImpersonation';
import AppLauncher from '../components/AppLauncher';
import {
  LayoutDashboard,
  BarChart3,
  Coins,
  Package,
  Settings,
  Settings2,
  User,
  AlertTriangle,
  Image,
  Building2,
  Sparkles,
  Volume2,
  Music,
} from 'lucide-react';
import { brandConfig } from '../config/brands';
import '../styles/admin.css';
import '../styles/admin-light-theme.css';

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const adminNavGroups: NavGroup[] = [
  {
    title: '概要',
    items: [
      { path: '/admin/dashboard', label: 'ダッシュボード', icon: <LayoutDashboard size={16} strokeWidth={1.5} /> },
      { path: '/admin/oem-billing', label: 'OEM利用状況', icon: <BarChart3 size={16} strokeWidth={1.5} /> },
    ],
  },
  {
    title: 'コンテンツ',
    items: [
      { path: '/admin/error-logs', label: 'エラーログ', icon: <AlertTriangle size={16} strokeWidth={1.5} /> },
      { path: '/admin/actors', label: brandConfig.labels.aiActors, icon: <User size={16} strokeWidth={1.5} /> },
      { path: '/admin/seedance-preset-actors', label: 'Seedance俳優', icon: <User size={16} strokeWidth={1.5} /> },
      { path: '/admin/sound-effects', label: '効果音カタログ', icon: <Volume2 size={16} strokeWidth={1.5} /> },
      { path: '/admin/free-bgm-tracks', label: 'フリー楽曲カタログ', icon: <Music size={16} strokeWidth={1.5} /> },
      { path: '/admin/media-specs', label: '媒体マスタ', icon: <Image size={16} strokeWidth={1.5} /> },
    ],
  },
  {
    title: '課金・ポイント',
    items: [
      { path: '/admin/plans', label: 'プラン', icon: <Package size={16} strokeWidth={1.5} /> },
      { path: '/admin/credit-rates', label: 'レート', icon: <Settings2 size={16} strokeWidth={1.5} /> },
      { path: '/admin/credit-adjustments', label: 'ポイント調整 監視', icon: <Coins size={16} strokeWidth={1.5} /> },
      { path: '/admin/acp-creators', label: 'クリエイタープログラム', icon: <Sparkles size={16} strokeWidth={1.5} /> },
      { path: '/admin/acp-statements', label: 'クリエイターRS集計', icon: <Coins size={16} strokeWidth={1.5} /> },
    ],
  },
  {
    title: 'システム',
    items: [{ path: '/admin/system', label: 'システム設定', icon: <Settings size={16} strokeWidth={1.5} /> }],
  },
];

/**
 * サイドバーのブランドロゴ。ライト/ダークで配色済みのラスタ画像を2枚とも
 * DOM に置き、中央テーマの汎用ユーティリティ (.lm-logo-light / .lm-logo-dark)
 * で data-lm-theme に応じて出し分ける。サイズは image-generator-bridge.css。
 * この /admin/* (LightMedia 運営組織用) では OEM ブランドロゴではなく、
 * 常にこの固定ロゴを使う (OEM ブランドは /org-admin 側の対象)。
 * 画像: frontend/public/logo-light.png / logo-dark.png
 */
function BrandLogo() {
  return (
    <>
      <img className="lm-logo lm-logo-light" src="/logo-light.png" alt="Image Generator" />
      <img className="lm-logo lm-logo-dark" src="/logo-dark.png" alt="Image Generator" />
    </>
  );
}

/** pathname からヘッダ左のページタイトルを導出する (nav 項目のラベルをそのまま使う)。 */
function pageTitleFor(pathname: string): string {
  for (const group of adminNavGroups) {
    for (const item of group.items) {
      if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
        return item.label;
      }
    }
  }
  return `${brandConfig.serviceName} 管理`;
}

/** アバターに表示するイニシャル (email 先頭2字を大文字で)。 */
function initialsFor(email: string): string {
  const trimmed = email.trim();
  if (!trimmed) return '?';
  return trimmed.slice(0, 2).toUpperCase();
}

function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return isActive ? 'lm-nav-link active' : 'lm-nav-link';
}

export default function AdminLayout() {
  const location = useLocation();
  const { isAuthenticated, isPlatformOrg, isAdmin, user } = useUser();
  const { isImpersonating, isLoading: impLoading } = useImpersonation();

  // モバイル (≤900px) のサイドバードロワー開閉。.lm-sidebar / .lm-scrim への
  // .is-open 付け替えはアプリ側の責務 (lightmedia-admin-theme の UX 規約)。
  const [drawerOpen, setDrawerOpen] = useState(false);
  // ヘッダ右上のマイページメニュー (.lm-menu-anchor) の開閉。
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement>(null);

  // ルート遷移のたびにモバイルドロワーは閉じる。
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // マイページメニューは外側クリックで閉じる (CSS はスタイルのみ提供する契約なので
  // 開閉・外側クリック検知は React 側で行う)。
  useEffect(() => {
    if (!menuOpen) return;
    function handlePointerDown(e: MouseEvent) {
      if (menuAnchorRef.current && !menuAnchorRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [menuOpen]);

  const closeDrawer = () => setDrawerOpen(false);

  // document.title は routes.tsx の handle.titleSuffix ('Creative Platform管理画面')
  // が定義元。DocumentTitleSync が解決する。

  // /admin/* 配下だけ lightmedia-admin-theme のダーク値を OS 設定に追従させる (opt-in)。
  // <html> に data-lm-theme を常時付与すると /admin 以外 (通常ユーザー向けダーク UI) の
  // color-scheme まで変わってしまうため、マウント中だけ 'auto' を設定し、
  // アンマウント時に必ず削除する。
  useEffect(() => {
    document.documentElement.dataset.lmTheme = 'auto';
    return () => {
      delete document.documentElement.dataset.lmTheme;
    };
  }, []);

  // iOS Safari の URL bar 周辺や overscroll で見える上下領域は <body> の背景色
  // を反映する (App.css の body { background: #000 } が黒帯の正体)。AdminLayout
  // 表示中だけ <html> / <body> の背景色と <meta name="theme-color"> を
  // --lm-bg (上の effect で data-lm-theme="auto" 適用済み。OS がダーク設定なら
  // ダーク値になる) に合わせる。アンマウント時に全て元へ戻す。
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const previousRootBg = root.style.backgroundColor;
    const previousBodyBg = body.style.backgroundColor;
    const lmBg = getComputedStyle(root).getPropertyValue('--lm-bg').trim() || '#fafafa';
    root.style.backgroundColor = lmBg;
    body.style.backgroundColor = lmBg;

    let themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const createdMeta = !themeColorMeta;
    const previousThemeColor = themeColorMeta?.content ?? '';
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.content = lmBg;

    return () => {
      root.style.backgroundColor = previousRootBg;
      body.style.backgroundColor = previousBodyBg;
      if (createdMeta) {
        themeColorMeta?.remove();
      } else if (themeColorMeta) {
        themeColorMeta.content = previousThemeColor;
      }
    };
  }, []);

  const handleLogout = useLogout();

  // 代行中は /admin 配下にアクセス不可 (バックエンドの prefix guard で 403)。
  // フロント側でもリダイレクトして中途半端な表示を避ける。
  if (!impLoading && isImpersonating) {
    return <Navigate to="/" replace />;
  }

  // LightMedia 運営組織の admin ユーザーのみ /admin を利用可能。
  // それ以外 (非運営組織、または運営組織でも role=user) は / にリダイレクト
  // (apiClient が 403 で token を削除してセッションが切れる副作用を避けるため、
  //  そもそも admin 配下に入らせない)。バックエンド require_platform_admin と対。
  if (isAuthenticated && (!isPlatformOrg || !isAdmin)) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="lm-shell admin-layout">
      <div className={drawerOpen ? 'lm-scrim is-open' : 'lm-scrim'} onClick={closeDrawer} />

      <aside className={drawerOpen ? 'lm-sidebar is-open' : 'lm-sidebar'}>
        <div className="lm-sidebar-brand">
          <BrandLogo />
        </div>
        <nav className="lm-nav">
          {adminNavGroups.map((group) => (
            <div key={group.title} className="lm-nav-group">
              <div className="lm-nav-group-label">{group.title}</div>
              {group.items.map((item) => (
                <NavLink key={item.path} to={item.path} className={navLinkClassName} onClick={closeDrawer}>
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
          <div className="lm-nav-group">
            <NavLink to="/org-admin" className={navLinkClassName} onClick={closeDrawer}>
              <Building2 size={16} strokeWidth={1.5} />
              パートナー管理画面
            </NavLink>
          </div>
        </nav>
      </aside>

      <div className="lm-main">
        <header className="lm-header">
          <button
            type="button"
            className="lm-menu-toggle"
            aria-label="メニューを開く"
            onClick={() => setDrawerOpen((v) => !v)}
          >
            ☰
          </button>
          <h1 className="lm-page-title">{pageTitleFor(location.pathname)}</h1>
          <div className="lm-header-actions">
            <Link to="/" className="lm-btn lm-btn-ghost lm-btn-sm">
              アプリに戻る
            </Link>

            <AppLauncher />

            <div className={menuOpen ? 'lm-menu-anchor is-open' : 'lm-menu-anchor'} ref={menuAnchorRef}>
              <button
                type="button"
                className="lm-avatar"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="マイページメニュー"
              >
                {user?.email ? initialsFor(user.email) : '?'}
              </button>
              <div className="lm-menu">
                {user?.email && (
                  <div className="lm-menu-item" style={{ cursor: 'default', fontWeight: 600 }}>
                    {user.email}
                    <span style={{ display: 'block', fontWeight: 400, color: 'var(--lm-muted)', fontSize: 12 }}>
                      platform admin
                    </span>
                  </div>
                )}
                <div className="lm-menu-separator" />
                <button
                  type="button"
                  className="lm-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    handleLogout();
                  }}
                >
                  ログアウト
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="lm-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
